import { useCallback, useEffect, useMemo } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/queries/queryClient";

import { AppRoutes } from "@/routing/Routes";
import { Toast } from "@/components/shared/Toast";
import { FlowProvider } from "@/contexts/FlowProvider";
import { AuthProvider } from "@/contexts/AuthProvider";
import { preloadFFmpeg } from "./utils/videoEditing";
import { DeployContextProvider } from "./contexts/DeployProvider";
import { TutorialProvider } from "@/contexts/TutorialProvider";
import { AnalyticsProvider } from "@/contexts/AnalyticsProvider";
import { FpsProvider } from "@/contexts/FpsProvider";
import { UpgradeModalProvider } from "@/contexts/UpgradeModalProvider";
import { ProjectSelectorProvider } from "@/contexts/ProjectSelectorProvider";
import { UrlModalProvider } from "@/contexts/UrlModalProvider";
import { WorkspaceProvider } from "@/contexts/WorkspaceProvider";
import { WorkflowProvider } from "@/contexts/WorkflowProvider";
import { useCIAlerts } from "@/hooks/useCIAlerts";
import { useAuth } from "@/contexts/AuthContext";
import { useCheckoutCompletion } from "@/hooks/useCheckoutCompletion";
import LoginModal from "./components/auth/LoginModal";
import { ExperimentProvider } from "@scripts/react/hooks/ExperimentContext";
import { SharedAnalyticsProvider } from "@scripts/react/hooks/AnalyticsContext";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { ActivitiesProvider } from "@scripts/react/components/Activities/ActivitiesContext";
import { ActivityEventKind } from "@scripts/react/components/Activities/sources/providers/types";
import { useAnalytics as useRapidAnalytics } from "@/contexts/AnalyticsContext";
import type { AnalyticsEvent } from "@/analytics/types";
import { createRapidFirestore } from "@/createRapidFirestore";
import { db } from "@/firebase/app";

export function RapidExperimentProvider({ children }: { children: React.ReactNode }) {
    const { user, guestUid } = useAuth();
    const { currentWorkspace } = useCurrentWorkspace();
    const groups = useMemo(
        () => (currentWorkspace?.id ? { workspace: currentWorkspace.id } : undefined),
        [currentWorkspace?.id]
    );

    return (
        <ExperimentProvider distinctId={user?.uid ?? guestUid ?? ""} groups={groups}>
            {children}
        </ExperimentProvider>
    );
}

const RAPID_ACTIVITY_KINDS = [ActivityEventKind.CIModelChange];

export function RapidActivitiesProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const { currentWorkspace } = useCurrentWorkspace();
    const firestore = useMemo(() => createRapidFirestore(db), []);

    return (
        <ActivitiesProvider
            workspaceId={currentWorkspace?.id ?? ""}
            userId={user?.uid ?? ""}
            firestore={firestore}
            kinds={RAPID_ACTIVITY_KINDS}
        >
            {children}
        </ActivitiesProvider>
    );
}

/**
 * Bridges Rapid's useAnalytics() into the shared analytics context
 * so cross-app components can call useSharedAnalytics().track().
 *
 * Must be rendered inside Rapid's AnalyticsProvider.
 */
function RapidSharedAnalyticsProvider({ children }: { children: React.ReactNode }) {
    const { track: rapidTrack } = useRapidAnalytics();

    const track = useCallback(
        (event: string, data?: Record<string, unknown>) => {
            rapidTrack({ event: event as AnalyticsEvent, data });
        },
        [rapidTrack]
    );

    return <SharedAnalyticsProvider track={track}>{children}</SharedAnalyticsProvider>;
}

export function App() {
    // Pre-load FFmpeg as soon as the app starts
    useEffect(() => {
        preloadFFmpeg() // FFmpeg preload completed - trimming will be instant
            .catch((error) => console.warn("🎬 [FFmpeg] Preload failed:", error));
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <ProjectSelectorProvider>
                    <AuthProvider>
                        <WorkspaceProvider>
                            <WorkflowProvider>
                                <RapidExperimentProvider>
                                    <RapidActivitiesProvider>
                                        <AnalyticsProvider>
                                            <RapidSharedAnalyticsProvider>
                                                <UpgradeModalProvider>
                                                    <FpsProvider>
                                                        <FlowProvider>
                                                            <DeployContextProvider>
                                                                <TutorialProvider>
                                                                    <UrlModalProvider>
                                                                        <AppContent />
                                                                    </UrlModalProvider>
                                                                </TutorialProvider>
                                                            </DeployContextProvider>
                                                        </FlowProvider>
                                                    </FpsProvider>
                                                </UpgradeModalProvider>
                                            </RapidSharedAnalyticsProvider>
                                        </AnalyticsProvider>
                                    </RapidActivitiesProvider>
                                </RapidExperimentProvider>
                            </WorkflowProvider>
                        </WorkspaceProvider>
                    </AuthProvider>
                </ProjectSelectorProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}

const AppContent = () => {
    const { isLoginModalOpen, closeLoginModal, guestAuthInitialized, initializeGuestAuth } =
        useAuth();

    // Initialize guest auth for all unauthenticated users
    // This enables API calls (like inference) to work without login
    useEffect(() => {
        if (!guestAuthInitialized) {
            initializeGuestAuth();
        }
    }, [guestAuthInitialized, initializeGuestAuth]);

    // Handle Stripe checkout completion redirect
    useCheckoutCompletion();

    // Listen for CI training evaluations and show toast alerts
    useCIAlerts();

    return (
        <>
            <AppRoutes />
            <Toast />
            <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
        </>
    );
};
